import React, { useState, useEffect, useMemo } from 'react';
import { formatAddress } from '../../../features/directions/directionsUtils';
import { stringToColor } from '../../../utils/avatarUtils';
import { formatPhoneNumber } from '../../utils/profileUtils';
import { useAccountSettingsStyleVars } from '../../../shared/hooks/useAccountSettingsStyleVars';
import '../../../components/settings/pages/AccountSettings/AccountSettings.css';
import '../../../shared/styles/accountFieldsFlat.css';
import ClientNotes from '../notes/ClientNotes';
import ClientAppointments from '../appointments/ClientAppointments';
import ClientProfileEditor from '../client-profile-editor/ClientProfileEditor';
import { useAuth } from '../../../context/AuthContext';
import { listAppointments } from '../../../api/appointments';
import { isClientArchived } from '../../../shared/clients/clientArchive';
import '../../../company-management/components/employee-profile/styles/employee-profile-workspace.css';
import { FormSkeleton } from '../../../components/loading/skeletons';
import './ClientProfile.css';

const normalizeSectionId = (id) => {
  let s = id === 'overview' ? 'profile' : id;
  if (s === 'sms') s = 'profile';
  if (!['profile', 'insights', 'appointments', 'notes'].includes(s)) s = 'profile';
  return s;
};

const ClientProfile = ({
  client,
  canEdit,
  formatDate,
  onClientUpdated,
  onSaveClient = async () => false,
  saving = false,
  onArchiveChange,
  archiving = false,
}) => {
  const { companyData } = useAuth();
  const accountFieldVars = useAccountSettingsStyleVars();
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [visitFrequency, setVisitFrequency] = useState('N/A');
  const [activeSection, setActiveSection] = useState('profile');
  const [draftArchived, setDraftArchived] = useState(false);

  useEffect(() => {
    setDraftArchived(isClientArchived(client));
  }, [client?.id, client?.archived]);

  const profileSections = useMemo(() => [
    { id: 'profile', label: 'Profile' },
    { id: 'insights', label: 'Insights' },
    { id: 'appointments', label: 'Appointments' },
    { id: 'notes', label: 'Notes' },
  ], []);

  useEffect(() => {
    if (!profileSections.some((s) => s.id === activeSection)) {
      setActiveSection('profile');
    }
  }, [profileSections, activeSection]);

  useEffect(() => {
    setActiveSection('profile');
  }, [client?.id]);

  const calculateVisitFrequency = (dates) => {
    if (dates.length < 2) return 'First Visit';
    const sortedDates = dates.sort((a, b) => a - b);
    const differences = [];
    for (let i = 1; i < sortedDates.length; i += 1) {
      const diffInDays = Math.round((sortedDates[i] - sortedDates[i - 1]) / (1000 * 60 * 60 * 24));
      differences.push(diffInDays);
    }
    const averageDays = Math.round(differences.reduce((a, b) => a + b, 0) / differences.length);
    if (averageDays < 7) return 'Weekly';
    if (averageDays < 14) return 'Bi-weekly';
    if (averageDays < 31) return 'Monthly';
    if (averageDays < 92) return 'Quarterly';
    if (averageDays < 365) return 'Yearly';
    return 'Irregular';
  };

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!companyData?.id || !client?.id) return;
      try {
        const result = await listAppointments({
          companyId: companyData.id,
          clientId: client.id,
          archived: 'all',
        });
        const appointments = result.appointments || [];
        const appointmentDates = [];
        let totalAmount = 0;
        for (const appointment of appointments) {
          if (appointment.dateTime) {
            const date = new Date(appointment.dateTime);
            if (date instanceof Date && !Number.isNaN(date.getTime())) appointmentDates.push(date.getTime());
          }
          if (String(appointment.status || '').toLowerCase() === 'completed' && appointment.totalPrice) {
            totalAmount += parseFloat(appointment.totalPrice) || 0;
          }
        }
        setTotalAppointments(appointments.length);
        setTotalSpent(totalAmount);
        setVisitFrequency(calculateVisitFrequency(appointmentDates));
      } catch {
        /* ignore */
      }
    };
    fetchAppointments();
  }, [companyData?.id, client?.id]);

  const setSection = (id) => {
    setActiveSection(normalizeSectionId(id));
  };

  const handleNoteAdded = () => {
    if (onClientUpdated) onClientUpdated();
  };

  const notesCount = Array.isArray(client?.notes) ? client.notes.length : 0;

  const sectionNavButtons = useMemo(
    () =>
      profileSections.map((section) => {
        const selected = activeSection === section.id;
        return (
          <button
            key={section.id}
            type="button"
            className={`employee-workspace__navItem ${selected ? 'is-active' : ''}`}
            onClick={() => setSection(section.id)}
          >
            {section.label}
            {section.id === 'notes' && notesCount > 0 ? (
              <span className="client-workspace__navBadge">{notesCount}</span>
            ) : null}
          </button>
        );
      }),
    [profileSections, activeSection, notesCount]
  );

  if (!client) {
    return <FormSkeleton fields={6} label="Loading client" />;
  }

  const fullName =
    `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Client';
  const initials =
    `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase() || '?';
  const avatarBgColor = stringToColor(fullName);
  const subtitle = 'Client';

  const companyLogoUrl = companyData?.logoUrl || companyData?.logo || null;
  const mainHeroShowCompanyLogo = !client.avatarUrl && Boolean(companyLogoUrl);
  const isArchived = isClientArchived(client);

  const handleArchiveToggle = (event) => {
    const nextActive = event.target.checked;
    if (!onArchiveChange) return;
    onArchiveChange(!nextActive);
  };

  const renderInsightsCards = () => (
    <div className="clientProfileInsightsGrid">
      <div className="clientProfileInsightCard">
        <svg
          className="clientProfileInsightIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <div className="clientProfileInsightValue clientProfileInsightValue--text">
          {formatDate(client.createdAt)}
        </div>
        <div className="clientProfileInsightLabel">Member since</div>
      </div>
      <div className="clientProfileInsightCard">
        <svg
          className="clientProfileInsightIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <div className="clientProfileInsightValue">{totalAppointments}</div>
        <div className="clientProfileInsightLabel">Total appointments</div>
      </div>
      <div className="clientProfileInsightCard">
        <svg
          className="clientProfileInsightIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
        <div className="clientProfileInsightValue">${totalSpent.toFixed(2)}</div>
        <div className="clientProfileInsightLabel">Total spent</div>
      </div>
      <div className="clientProfileInsightCard">
        <svg
          className="clientProfileInsightIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <div className="clientProfileInsightValue">Coming soon</div>
        <div className="clientProfileInsightLabel">Loyalty rewards</div>
      </div>
      <div className="clientProfileInsightCard">
        <svg
          className="clientProfileInsightIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <div className="clientProfileInsightValue">{visitFrequency}</div>
        <div className="clientProfileInsightLabel">Typical visit cadence</div>
      </div>
    </div>
  );

  const renderSectionContent = () => {
    if (activeSection === 'insights') {
      return renderInsightsCards();
    }

    if (activeSection === 'profile') {
      return (
        <>
          {canEdit ? (
            <ClientProfileEditor client={client} onSave={onSaveClient} saving={saving} />
          ) : (
            <div className="accountFieldsFlat" style={accountFieldVars}>
              <section className="asCard" aria-label="Contact">
                <div className="asCardHeader">
                  <h2>Contact</h2>
                  <p className="sub">How to reach this client.</p>
                </div>
                <div className="asCardBody">
                  <div className="asFormGrid">
                    <div className="asField">
                      <div className="asFieldLabelRow">
                        <span className="asLabel">Email</span>
                      </div>
                      <p className="asReadonly">{client.email || 'No email provided'}</p>
                    </div>
                    <div className="asField">
                      <div className="asFieldLabelRow">
                        <span className="asLabel">Phone</span>
                      </div>
                      <p className="asReadonly">
                        {client.phone ? formatPhoneNumber(client.phone) : 'No phone number provided'}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="asCard" aria-label="Address">
                <div className="asCardHeader">
                  <h2>Address</h2>
                  <p className="sub">Service location and mailing details.</p>
                </div>
                <div className="asCardBody">
                  {formatAddress(client.address) ? (
                    <div className="asFormGrid asFormGrid--three">
                      <div className="asField" style={{ gridColumn: '1 / -1' }}>
                        <div className="asFieldLabelRow">
                          <span className="asLabel">Street address</span>
                        </div>
                        <p className="asReadonly">
                          {[client.address?.street1, client.address?.street2].filter(Boolean).join(', ') || '—'}
                        </p>
                      </div>
                      <div className="asField">
                        <div className="asFieldLabelRow">
                          <span className="asLabel">City</span>
                        </div>
                        <p className="asReadonly">{client.address?.city || '—'}</p>
                      </div>
                      <div className="asField">
                        <div className="asFieldLabelRow">
                          <span className="asLabel">State</span>
                        </div>
                        <p className="asReadonly">{client.address?.state || '—'}</p>
                      </div>
                      <div className="asField">
                        <div className="asFieldLabelRow">
                          <span className="asLabel">ZIP code</span>
                        </div>
                        <p className="asReadonly">{client.address?.zipCode || '—'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="asHelperText" style={{ margin: 0 }}>
                      No address on file.
                    </p>
                  )}
                </div>
              </section>

              <section className="asCard" aria-label="Emergency contact">
                <div className="asCardHeader">
                  <h2>Emergency contact</h2>
                  <p className="sub">Someone we can reach if needed.</p>
                </div>
                <div className="asCardBody">
                  {client.emergencyContact?.name ? (
                    <div className="asFormGrid">
                      <div className="asField">
                        <div className="asFieldLabelRow">
                          <span className="asLabel">Name</span>
                        </div>
                        <p className="asReadonly">{client.emergencyContact.name}</p>
                      </div>
                      <div className="asField">
                        <div className="asFieldLabelRow">
                          <span className="asLabel">Phone</span>
                        </div>
                        <p className="asReadonly">
                          {client.emergencyContact.phone
                            ? formatPhoneNumber(client.emergencyContact.phone)
                            : 'No phone number provided'}
                        </p>
                      </div>
                      <div className="asField" style={{ gridColumn: '1 / -1' }}>
                        <div className="asFieldLabelRow">
                          <span className="asLabel">Relationship</span>
                        </div>
                        <p className="asReadonly">
                          {client.emergencyContact.relationship || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="asHelperText" style={{ margin: 0 }}>
                      No emergency contact provided.
                    </p>
                  )}
                </div>
              </section>
            </div>
          )}
        </>
      );
    }

    if (activeSection === 'appointments') {
      return <ClientAppointments client={client} />;
    }
    if (activeSection === 'notes') {
      return <ClientNotes client={client} onNoteAdded={handleNoteAdded} />;
    }
    return null;
  };

  return (
    <div
      className={`employee-workspace employee-workspace--clientProfile${
        activeSection === 'insights' ? ' employee-workspace--insightsSection' : ''
      }${activeSection === 'notes' ? ' employee-workspace--notesSection' : ''}${
        activeSection === 'appointments' ? ' employee-workspace--appointmentsSection' : ''
      }`}
    >
      <div className="employee-workspace__grid">
        <aside className="employee-workspace__sidebar">
          <div className="employee-workspace__summary">
            <div
              className="employee-workspace__avatar"
              aria-hidden={!client.avatarUrl}
              style={
                !client.avatarUrl
                  ? {
                      background: avatarBgColor,
                      borderColor: 'rgba(255, 255, 255, 0.35)',
                      color: '#fff',
                    }
                  : undefined
              }
            >
              {client.avatarUrl ? <img src={client.avatarUrl} alt={fullName} /> : initials}
            </div>
            <h2 className="employee-workspace__name">{fullName}</h2>
            <p className="employee-workspace__title">{subtitle}</p>
            {canEdit ? (
              <div
                className={`employee-workspace__statusRow ${
                  !draftArchived ? 'is-active' : 'is-inactive'
                }`}
              >
                <span>Archived</span>
                <input
                  type="checkbox"
                  className="employee-workspace__toggleControl employee-workspace__toggleControl--compact"
                  checked={!draftArchived}
                  disabled={activeSection !== 'profile' || saving || archiving}
                  onChange={handleArchiveToggle}
                  aria-label="Client archive status"
                />
                <span>Active</span>
              </div>
            ) : null}
          </div>

          <nav
            className="employee-workspace__nav employee-workspace__nav--sidebar"
            aria-label="Client profile sections"
          >
            {sectionNavButtons}
          </nav>
        </aside>

        <section className="employee-workspace__main">
          <div className="employee-workspace__mainHeader employee-workspace__mainHeader--hero">
            <div className="employee-workspace__mainHero">
              <div
                className={`employee-workspace__mainHeroAvatar ${
                  mainHeroShowCompanyLogo ? 'employee-workspace__mainHeroAvatar--logo' : ''
                }`}
              >
                {client.avatarUrl ? (
                  <img src={client.avatarUrl} alt={fullName} />
                ) : companyLogoUrl ? (
                  <img
                    src={companyLogoUrl}
                    alt={`${companyData?.companyName || companyData?.name || 'Company'} logo`}
                  />
                ) : (
                  <span
                    className="employee-workspace__mainHeroAvatarFallback"
                    style={{ background: avatarBgColor, color: '#fff' }}
                  >
                    {initials}
                  </span>
                )}
              </div>
              <h1 className="employee-workspace__heading">{fullName}</h1>
            </div>
          </div>

          <nav
            className="employee-workspace__nav employee-workspace__nav--main"
            aria-label="Client profile sections"
          >
            {sectionNavButtons}
          </nav>

          <div className="employee-workspace__content">
            {isArchived ? (
              <div className="clientProfileArchivedBanner" role="status">
                This client is archived and hidden from active lists.
              </div>
            ) : null}
            {renderSectionContent()}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClientProfile;
